import { useState, useEffect } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { GuardedDialog, isDirty } from "@/components/support/GuardedDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { TimeEntryDialog } from "@/components/support/TimeEntryDialog";
import { TimeEntryDetailDialog } from "@/components/support/TimeEntryDetailDialog";
import { BILLING_STATUSES, BILLING_COLOR, LOCATION_TYPES, fmtDuration, fmtDate } from "@/lib/supportApi";
import { Plus, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const NO_TICKET = "__none__";

export default function TimeEntries() {
  const { currentOrgId, currentOrg } = useAuth();
  const isAdmin = currentOrg && (currentOrg.role === "owner" || currentOrg.role === "admin");
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [e, setE] = useState({});
  const [eInit, setEInit] = useState({});

  const load = () => { if (currentOrgId) api.get(`/support/time-entries?org_id=${currentOrgId}${filter !== "all" ? `&billing_status=${filter}` : ""}`).then((r) => setRows(r.data)).catch(() => {}); };
  useEffect(load, [currentOrgId, filter]); // eslint-disable-line

  useEffect(() => {
    if (!currentOrgId) return;
    api.get(`/support/customers?org_id=${currentOrgId}&status=active`).then((r) => setCustomers(r.data)).catch(() => {});
    api.get(`/support/categories?org_id=${currentOrgId}`).then((r) => setCategories(r.data)).catch(() => {});
  }, [currentOrgId]);

  // tickets for the customer currently selected in the correction dialog
  useEffect(() => {
    if (edit && e.customer_id && currentOrgId) {
      api.get(`/support/tickets?org_id=${currentOrgId}&customer_id=${e.customer_id}`).then((r) => setTickets(r.data)).catch(() => setTickets([]));
    } else setTickets([]);
  }, [edit, e.customer_id, currentOrgId]);

  const isLinked = !!(edit && edit.remote_session_id);

  const openEdit = (row) => {
    setEdit(row);
    const v = {
      customer_id: row.customer_id || "",
      ticket_id: row.ticket_id || NO_TICKET,
      description: row.description || "",
      notes: row.notes || "",
      category_key: row.category_key || "",
      location_type: row.location_type || "remote",
      minutes: Math.round((row.actual_seconds || 0) / 60),
      billing_status: row.billing_status || "unclassified",
      reason: "",
    };
    setE(v); setEInit(v);
  };
  const set = (k, v) => setE((p) => ({ ...p, [k]: v }));

  const buildBody = () => ({
    org_id: currentOrgId, reason: e.reason.trim(),
    customer_id: e.customer_id || null,
    ticket_id: e.ticket_id === NO_TICKET ? "" : e.ticket_id,
    description: e.description, notes: e.notes,
    category_key: e.category_key || null, location_type: e.location_type,
    actual_seconds: Math.round(parseFloat(e.minutes || "0") * 60),
    billing_status: e.billing_status,
  });

  const saveEdit = async () => {
    if (!e.reason.trim()) { toast.error("A reason is required for corrections"); return; }
    setSaving(true);
    try { await api.patch(`/support/time-entries/${edit.entry_id}`, buildBody()); toast.success("Entry corrected"); setEdit(null); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  const voidEntry = async () => {
    if (!e.reason.trim()) { toast.error("Reason required to void"); return; }
    setSaving(true);
    try { await api.patch(`/support/time-entries/${edit.entry_id}`, { org_id: currentOrgId, status: "voided", voided_reason: e.reason.trim(), reason: e.reason.trim() }); toast.success("Entry voided"); setEdit(null); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not void"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="time-entries-page">
      <div className="flex items-center justify-between gap-3">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 bg-black border-zinc-800"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            <SelectItem value="all">All classifications</SelectItem>
            {BILLING_STATUSES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button data-testid="add-time-entry-button" onClick={() => setAddOpen(true)} className="bg-blue-600 hover:bg-blue-500"><Plus size={15} className="mr-1.5" /> Add time</Button>
      </div>
      <div className="rounded-xl border border-white/10 bg-zinc-900/60 divide-y divide-white/5">
        {rows.map((r) => (
          <div key={r.entry_id} data-testid={`time-entry-row-${r.entry_id}`} onClick={() => setDetailRow(r)} className={`flex items-center justify-between gap-3 px-4 py-3 cursor-pointer hover:bg-white/5 transition-colors ${r.status === "voided" ? "opacity-40 line-through" : ""}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span data-testid={`time-entry-customer-${r.entry_id}`} className="text-sm font-medium text-zinc-100 truncate">{r.customer_name || <span className="text-zinc-500 italic font-normal">No customer</span>}</span>
                {r.ticket_number != null && <span data-testid={`time-entry-ticket-${r.entry_id}`} className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-blue-500/30 bg-blue-500/10 text-blue-300">#{r.ticket_number}</span>}
              </div>
              <div className="text-sm text-zinc-300 truncate">{r.description || "(no description)"}</div>
              <div className="text-xs text-zinc-500">{fmtDate(r.started_at)} · {r.source} · actual {fmtDuration(r.actual_seconds)} {r.corrected && <span className="text-amber-400">· corrected</span>}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${BILLING_COLOR[r.billing_status]}`}>{r.billing_status}</span>
              {isAdmin && r.status !== "voided" && <button data-testid={`edit-entry-${r.entry_id}`} onClick={(ev) => { ev.stopPropagation(); openEdit(r); }} className="p-1.5 rounded hover:bg-white/10"><PencilSimple size={14} /></button>}
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="p-8 text-center text-sm text-zinc-500">No time entries</div>}
      </div>

      <TimeEntryDialog open={addOpen} onOpenChange={setAddOpen} mode="manual" onSaved={load} />

      <TimeEntryDetailDialog
        open={!!detailRow}
        onOpenChange={(o) => !o && setDetailRow(null)}
        entryId={detailRow?.entry_id}
        categories={categories}
        onEdit={() => { const r = detailRow; setDetailRow(null); openEdit(r); }}
      />

      <GuardedDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} dirty={isDirty(e, eInit)} className="bg-zinc-900 border-white/10 max-w-lg" testid="correction-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Correct time entry</DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">Admins may correct any field below. Every change needs a reason and is kept in the audit history.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1 max-h-[62vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Customer</Label>
                <Select value={e.customer_id} onValueChange={(v) => { set("customer_id", v); set("ticket_id", NO_TICKET); }} disabled={isLinked}>
                  <SelectTrigger data-testid="correction-customer" className="mt-1 bg-black border-zinc-800"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {customers.map((c) => <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {isLinked && <div className="text-[11px] text-amber-400 mt-1">Locked — linked to a remote session</div>}
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Associated ticket</Label>
                <Select value={e.ticket_id} onValueChange={(v) => set("ticket_id", v)}>
                  <SelectTrigger data-testid="correction-ticket" className="mt-1 bg-black border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value={NO_TICKET}>No ticket</SelectItem>
                    {tickets.map((t) => <SelectItem key={t.ticket_id} value={t.ticket_id}>#{t.ticket_number} {t.subject}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Work description</Label>
              <Textarea data-testid="correction-description" value={e.description} onChange={(ev) => set("description", ev.target.value)} rows={2} className="mt-1 bg-black border-zinc-800" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Category</Label>
                <Select value={e.category_key} onValueChange={(v) => set("category_key", v)}>
                  <SelectTrigger data-testid="correction-category" className="mt-1 bg-black border-zinc-800"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {categories.map((c) => <SelectItem key={c.category_key} value={c.category_key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Location</Label>
                <Select value={e.location_type} onValueChange={(v) => set("location_type", v)}>
                  <SelectTrigger data-testid="correction-location" className="mt-1 bg-black border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {LOCATION_TYPES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Actual minutes</Label>
                <Input data-testid="correction-minutes" type="number" min="0" value={e.minutes} onChange={(ev) => set("minutes", ev.target.value)} className="mt-1 bg-black border-zinc-800" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Billing</Label>
                <Select value={e.billing_status} onValueChange={(v) => set("billing_status", v)}>
                  <SelectTrigger data-testid="correction-billing" className="mt-1 bg-black border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">{BILLING_STATUSES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Notes</Label>
              <Textarea data-testid="correction-notes" value={e.notes} onChange={(ev) => set("notes", ev.target.value)} rows={2} className="mt-1 bg-black border-zinc-800" />
            </div>
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
              <Label className="text-xs text-amber-400">Reason for correction (required — kept in audit history)</Label>
              <Input data-testid="correction-reason" value={e.reason} onChange={(ev) => set("reason", ev.target.value)} placeholder="Separate from the work description" className="mt-1 bg-black border-zinc-800" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" data-testid="correction-void" onClick={voidEntry} disabled={saving} className="text-red-400 hover:text-red-300 mr-auto">Void entry</Button>
            <DialogClose asChild><Button variant="ghost" data-testid="correction-cancel" className="text-zinc-400">Cancel</Button></DialogClose>
            <Button data-testid="correction-save" onClick={saveEdit} disabled={saving} className="bg-blue-600 hover:bg-blue-500">{saving ? "Saving…" : "Save correction"}</Button>
          </DialogFooter>
      </GuardedDialog>
    </div>
  );
}
